import { computed, inject, Injectable } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { ActivatedRouteSnapshot, NavigationEnd, Router } from "@angular/router";
import { filter, map, startWith } from "rxjs";
import { routeData } from "./route-data";

type CastState = { castable: boolean; casting: boolean; bare: boolean };

function castState(root: ActivatedRouteSnapshot): CastState {
  let castable = false;
  let bare = false;
  for (let route: ActivatedRouteSnapshot | null = root; route; route = route.firstChild) {
    castable ||= !!routeData(route).castable;
    bare = !!routeData(route).bareLayout;
  }
  return { castable, casting: castable && root.queryParamMap.get("cast") === "1", bare };
}

/**
 * Cast mode: a castable page (data.castable) with ?cast=1 drops the nav and header so it can be shown full-screen on
 * a TV. The header's cast button turns it on; the layout's exit button (or removing the param) turns it off.
 */
@Injectable({ providedIn: "root" })
export class CastMode {
  #router = inject(Router);

  #state = toSignal(
    this.#router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      startWith(null),
      map(() => castState(this.#router.routerState.snapshot.root))
    ),
    { requireSync: true }
  );

  readonly castable = computed(() => this.#state().castable);
  readonly casting = computed(() => this.#state().casting);
  /** No nav or header: casting, or a route that is always bare (data.bareLayout). */
  readonly bare = computed(() => this.#state().casting || this.#state().bare);

  start() {
    void this.#router.navigate([], { queryParams: { cast: 1 }, queryParamsHandling: "merge" });
  }

  stop() {
    void this.#router.navigate([], { queryParams: { cast: null }, queryParamsHandling: "merge" });
  }
}
